import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { API } from '@app-core/constants/api.constants';
import { DVR_STATUS_LIST, EVENTS_CONFIG } from '@app-core/constants/constants';
import { setParams } from '@app-core/models/core.model';
import { HttpCacheService } from '@app-core/services/cache/cache.service';
import { DataService } from '@app-core/services/data/data.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import {
  CreateLocalDVRRequestBody,
  LocalDVRDetails,
  VideoRequestsPageEventType,
  VideoRequestsPageQueryParams,
} from '@app-dvr/common/dvr.model';
import { IncidentModalComponent } from '@app-shared/components/incident-modal/incident-modal.component';
import { GetDvrDetailsParams, GetEventDetailsParams } from '@app-trip-details/common/trip-details.model';
import { CLIENT_CONFIG } from '@config/config';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DvrService {
  private dvrRequestType: string;
  // private eventsConfig = EVENTS_CONFIG;
  private eventsConfig;
  public localDVRDetails: LocalDVRDetails;

  constructor(
    private http: HttpClient,
    private cacheService: HttpCacheService,
    private dataService: DataService,
    public dialog: MatDialog,
    public snackbarService: SnackBarService,
    public translate: TranslateService
  ) {
    const combinedEventsList = this.dataService.modifyFleeEvents();
    const modifiedEventsConfig = this.dataService.transformObject(combinedEventsList);
    this.eventsConfig = { ...EVENTS_CONFIG, ...modifiedEventsConfig };
  }

  public createGenericDvr(params: any): Observable<any> {
    return this.http.post(API.CREATE_DVR_REQUEST, params);
  }

  private parseDvrEvent(dvr: any, index: number) {
    const {
      startTime = '',
      startTimeUTC = '',
      endTimeUTC = '',
      createdAt = '',
      updatedAt = '',
      isViewed = false,
      timelapseEnabled = false,
      dvrVideoType = '',
      status = '',
      firstLocation: { latitude = 0, longitude = 0 } = {},
    } = dvr;
    const mediaFiles = dvr.response?.mediaFiles || [];

    const dvrDuration = Math.ceil(new Date(endTimeUTC).getTime() - new Date(startTimeUTC).getTime()) / 1000;
    switch (dvrVideoType) {
      case 'TVI_MDVR':
        this.dvrRequestType = 'MDVR';
        break;
      default:
        this.dvrRequestType = timelapseEnabled ? 'Time-lapse Video Request' : 'Video Request';
        break;
    }
    let dvrStatus: any;
    switch (status) {
      case 'PENDING':
      case 'ACKNOWLEDGED':
      case 'STARTED':
        dvrStatus = DVR_STATUS_LIST.PROCESSING;
        break;
      case 'FINISHED':
        dvrStatus = DVR_STATUS_LIST.FINISHED;
        break;
      case 'UNAVAILABLE':
        dvrStatus = DVR_STATUS_LIST.UNAVAILABLE;
        break;
      case 'FAILED':
        dvrStatus = DVR_STATUS_LIST.FAILED;
        break;
      case 'CANCELED':
        dvrStatus = DVR_STATUS_LIST.CANCELED;
        break;
      case 'PENDING_FOR_REVIEW':
        dvrStatus = DVR_STATUS_LIST.PENDING_FOR_REVIEW;
        break;
      default:
        dvrStatus = DVR_STATUS_LIST.PROCESSING;
        break;
    }
    let isAudioEnabled = false;
    if (mediaFiles.length) {
      isAudioEnabled = mediaFiles[0]?.videoDetails?.audioEnabled || mediaFiles[1]?.videoDetails?.audioEnabled;
    }
    const updatedDvr = {
      ...dvr,
      latitude,
      longitude,
      isAudioEnabled,
      dvrDuration,
      eventTypeLabel: dvr.timelapseEnabled ? 'Time-Lapse Video Request' : 'Video Request',
      eventVideoFilename: dvr.response.link,
      eventVideoFile: dvr.response.link,
      mediaFiles,
      showCoachingCompleted: false,
      positionIndex: index,
      dvrRequestType: this.dvrRequestType,
      isViewed,
      status: dvrStatus,
      isDvrEvent: true,
      videoDetails: {
        videoResolution: dvr.videoResolution,
        audioEnabled: isAudioEnabled,
      },
    };
    if (startTime && startTimeUTC && createdAt && updatedAt) {
      const timeDiff = new Date(startTime).getTime() - new Date(startTimeUTC).getTime();
      const createdAtLocal = new Date(new Date(createdAt).getTime() + timeDiff);
      const updatedAtLocal = new Date(new Date(updatedAt).getTime() + timeDiff);
      return {
        ...updatedDvr,
        createdAtLocal,
        updatedAtLocal,
      };
    }
    return updatedDvr;
  }

  private parseExternalEvent(eventDetails: any, index: number) {
    const { eventVideoFilenameUploadStatus = {} } = eventDetails;
    const videoStatus = eventVideoFilenameUploadStatus.status === 'UPLOADED' ? 'FINISHED' : 'UPLOAD PENDING';
    return {
      ...eventDetails,
      status: {
        type: videoStatus,
      },
      isExternalEvent: true,
      eventTypeLabel: CLIENT_CONFIG.externalEventsLabel,
      positionIndex: index,
      eventVideoFilename: eventDetails.eventVideoFile,
    };
  }

  public getFleetDvrRequests(params: any, isRefresh?: boolean): Observable<any> {
    const httpOptions = {
      params: setParams(params),
    };
    if (isRefresh) {
      this.cacheService.burstCache$.next(API.GET_DVR_REQUESTS);
    }
    return this.http.get(API.GET_DVR_REQUESTS, httpOptions).pipe(
      map((res: any) => {
        const { uploadRequests = [] } = res.data || {};
        return {
          ...res,
          data: {
            ...res.data,
            uploadRequests: uploadRequests.map((dvr, index) => {
              return this.parseDvrEvent(dvr, index);
            }),
          },
        };
      })
    );
  }

  public getFleetExternalEvents(params: any, isRefresh?: boolean): Observable<any> {
    const httpOptions = {
      params: setParams(params),
    };
    if (isRefresh) {
      this.cacheService.burstCache$.next(API.GET_EXTERNAL_EVENTS);
    }
    return this.http.get(API.GET_EXTERNAL_EVENTS, httpOptions).pipe(
      map((res: any) => {
        const externalEvents = res.rows || [];
        return {
          ...res,
          rows: externalEvents.map((x: any, index: number) => {
            return this.parseExternalEvent(x, index);
          }),
        };
      })
    );
  }

  public getFleetEdvrRequests(params: any, isRefresh?: boolean): Observable<any> {
    const httpOptions = {
      params: setParams(params),
    };
    if (isRefresh) {
      this.cacheService.burstCache$.next(API.GET_EDVR_REQUESTS);
    }
    return this.http.get(API.GET_EDVR_REQUESTS, httpOptions).pipe(
      map((res: any) => {
        const { edvrRequests = [] } = res.data || {};
        return {
          ...res,
          data: {
            ...res.data,
            edvrRequests: edvrRequests
              .filter((event: any) => {
                const { eventType } = event || {};
                return this.eventsConfig.hasOwnProperty(eventType);
              })
              .map((edvr: any, index: number) => {
                const { label = '' } = this.eventsConfig[edvr.eventType] || {};
                const { eventTimestampUTC = '', eventTimestamp = '', createdAt = '', status = '', updatedAt = '' } = edvr;
                let edvrStatus: any;
                switch (status) {
                  case 'PENDING':
                  case 'ACKNOWLEDGED':
                  case 'STARTED':
                    edvrStatus = DVR_STATUS_LIST.PROCESSING;
                    break;
                  case 'FINISHED':
                    edvrStatus = DVR_STATUS_LIST.FINISHED;
                    break;
                  case 'UNAVAILABLE':
                    edvrStatus = DVR_STATUS_LIST.UNAVAILABLE;
                    break;
                  case 'FAILED':
                    edvrStatus = DVR_STATUS_LIST.FAILED;
                    break;
                  case 'CANCELED':
                    edvrStatus = DVR_STATUS_LIST.CANCELED;
                    break;
                  default:
                    edvrStatus = DVR_STATUS_LIST.PROCESSING;
                    break;
                }
                if (eventTimestamp && eventTimestampUTC && createdAt && updatedAt) {
                  const timeDiff = new Date(eventTimestamp).getTime() - new Date(eventTimestampUTC).getTime();
                  const createdAtLocal = new Date(new Date(createdAt).getTime() + timeDiff);
                  const updatedAtLocal = new Date(new Date(updatedAt).getTime() + timeDiff);
                  return {
                    ...edvr,
                    createdAtLocal,
                    updatedAtLocal,
                    eventTypeLabel: label,
                    positionIndex: index,
                    isEdvrEvent: true,
                    status: edvrStatus,
                  };
                }
                return {
                  ...edvr,
                  eventTypeLabel: label,
                  positionIndex: index,
                  isEdvrEvent: true,
                  status: edvrStatus,
                };
              }),
          },
        };
      })
    );
  }

  public createLocalDvrRequest(body: CreateLocalDVRRequestBody): Observable<any> {
    return this.http.post(API.CREATE_LOCAL_DVR_REQUEST, body).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(`${API.GET_DVR_REQUESTS}`);
      })
    );
  }

  public getExternalEventDetails(params: GetEventDetailsParams) {
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(API.EVENT_DETAILS, httpOptions).pipe(
      map((dvr) => {
        return this.parseExternalEvent(dvr, 0);
      })
    );
  }

  public getDvrDetails(params: GetDvrDetailsParams) {
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(API.DVR_DETAILS, httpOptions).pipe(
      map((dvr) => {
        return this.parseDvrEvent(dvr, 0);
      })
    );
  }

  public showMedia(eventDetails: any) {
    this.dialog.open(IncidentModalComponent, {
      panelClass: ['incident-modal', 'mobile-modal'],
      position: { top: '24px', bottom: '24px' },
      autoFocus: false,
      disableClose: true,
      data: {
        source: 'DVR',
        allEvents: [eventDetails],
      },
    });
  }

  public getEventDetailsAndOpenDialog(params: VideoRequestsPageQueryParams) {
    let API$: Observable<any>;
    if (params.eventType === VideoRequestsPageEventType.DVR && params.uploadRequestId) {
      API$ = this.getDvrDetails(new GetDvrDetailsParams(params));
    } else if (params.eventType === VideoRequestsPageEventType.PANIC_BUTTON && params.eventIndex) {
      API$ = this.getExternalEventDetails(new GetEventDetailsParams({ ...params, eventIndex: Number(params.eventIndex) }));
    }

    return API$.pipe(
      tap((eventDetails) => {
        this.showMedia(eventDetails);
      }),
      catchError(() => {
        this.snackbarService.failure(this.translate.instant('notificationConfiguration.Unable to find the event details at this moment'));
        return of(null);
      })
    );
  }
}
